export type BrandConnectionStatus =
  | "connected"
  | "needs-reauthorization"
  | "sync-issue";
export type AgentBrandScope = "all-brands" | string;

export type BrandConnection = {
  id: string;
  provider: string;
  accountName: string;
  accountDetail: string;
  brandId: string | null;
  brandIds: string[];
  status: BrandConnectionStatus;
  /** Server-provided display name for the connector after owner identities are redacted. */
  connectedByName?: string;
  connectedByUserId: string;
  connectedAt: string;
  assignedByUserId: string | null;
  assignedAt: string | null;
  /** API-backed connection capability flags used by invite access management. */
  canUseConnection?: boolean;
  canManageConnection?: boolean;
  canManageAccess?: boolean;
};

export type CreateBrandConnectionInput = Pick<
  BrandConnection,
  "provider" | "accountName" | "accountDetail"
>;

export type BrandUser = {
  id: string;
  name: string;
  email: string;
  /** Uses the same member avatar source as Workspace member management. */
  logoUrl?: string;
  role: "Owner" | "Admin" | "Member";
};

/** @deprecated Brand roles are retained only to migrate older prototype data. */
export type BrandRole = "Member" | "Owner" | "VIEW" | "EDIT";

export type BrandMembership = {
  brandId: string;
  userId: string;
  role: BrandRole;
};

export type BrandMember = BrandUser & {
  brandRole: BrandRole;
};

export type BrandConnectionAccessLevel = "no_access" | "read" | "action";

/** Every grant belongs to one concrete application account inside one Brand. */
export type BrandConnectionAccessGrant = {
  /** Access is scoped to the Brand and its single connection assignment. */
  brandId: string;
  connectionId: string;
  userId: string;
  accessLevel: BrandConnectionAccessLevel;
  /**
   * Lets the connection owner delegate this Project's member-access management
   * for the app account. It is separate from this user's app-use access.
   */
  canManageAccess?: boolean;
};

/** Access selected before an invited Workspace user has a stable user id. */
export type PendingBrandConnectionAccessGrant = {
  email: string;
  brandId: string;
  connectionId: string;
  accessLevel: BrandConnectionAccessLevel;
  /** Lets an invited user manage access after they join, even without app usage access. */
  canManageAccess?: boolean;
};

export type BrandRecord = {
  id: string;
  /** The Workspace-wide Brand that every active Workspace user can enter. */
  isDefault?: boolean;
  scope?: "workspace" | "private";
  /** Project visibility returned by the Project API. */
  visibility?: "WORKSPACE" | "PRIVATE";
  name: string;
  domain: string;
  market: string;
  description: string;
  logoUrl: string | null;
  accent: string;
  createdByUserId?: string;
  /**
   * Legacy storage field. Brand management now follows each member's Can edit
   * access; the creator remains the immutable lifecycle record.
   */
  primaryOwnerUserId?: string;
};

export type BrandPrototypeData = {
  brands: BrandRecord[];
  connections: BrandConnection[];
  users: BrandUser[];
  memberships: BrandMembership[];
  connectionAccessGrants: BrandConnectionAccessGrant[];
  pendingInvitationAccessGrants?: PendingBrandConnectionAccessGrant[];
  externalConnectionBrandIds: Record<string, string[]>;
  /** Labels are owned by the app-account × Business group assignment. */
  externalConnectionBrandLabels?: Record<string, Record<string, string>>;
  /**
   * Google Ads can expose several customer accounts through one OAuth
   * connection. Brand association belongs to the customer account, not to the
   * OAuth connection, so this map is keyed by `${connectionKey}:${customerId}`.
   */
  externalGoogleAdsCustomerBrandIds: Record<string, string[]>;
  externalGoogleAdsCustomerBrandLabels?: Record<string, Record<string, string>>;
  activeAgentBrandId: AgentBrandScope;
};

export const INITIAL_BRAND_PROTOTYPE_DATA: BrandPrototypeData = {
  brands: [],
  connections: [],
  users: [],
  memberships: [],
  connectionAccessGrants: [],
  pendingInvitationAccessGrants: [],
  externalConnectionBrandIds: {},
  externalConnectionBrandLabels: {},
  externalGoogleAdsCustomerBrandIds: {},
  externalGoogleAdsCustomerBrandLabels: {},
  activeAgentBrandId: "all-brands",
};

const uniqueBrandIds = (brandIds: string[]) =>
  Array.from(new Set(brandIds.filter(Boolean)));

export const cloneBrandPrototypeData = (
  data: BrandPrototypeData = INITIAL_BRAND_PROTOTYPE_DATA,
): BrandPrototypeData => {
  const connections = data.connections.map((connection) => {
    const savedBrandIds = Array.isArray(connection.brandIds)
      ? uniqueBrandIds(connection.brandIds)
      : connection.brandId
        ? [connection.brandId]
        : [];
    const brandId = savedBrandIds[0] || null;
    return {
      ...connection,
      // A connected account can be used by more than one Brand. `brandId`
      // stays as the legacy primary assignment for older callers.
      brandIds: savedBrandIds,
      brandId,
    };
  });

  const memberships = data.memberships.map((membership) => {
    const legacyRole = membership.role as string;
    return {
      ...membership,
      // "Lead" and the older "Manager" both mean Can edit in the current
      // Brand access model.
      role:
        legacyRole === "Lead" || legacyRole === "Manager"
          ? "Owner"
          : membership.role,
    };
  });
  const defaultBrandId =
    data.brands.find((brand) => brand.isDefault)?.id ||
    data.brands[0]?.id ||
    null;
  const brands = data.brands.map((brand) => {
    const membershipOwner = memberships.find(
      (membership) =>
        membership.brandId === brand.id && membership.role === "Owner",
    )?.userId;
    const createdByUserId =
      brand.createdByUserId || brand.primaryOwnerUserId || membershipOwner;
    return {
      ...brand,
      // Older drafts used the mutable Primary owner field in place of the
      // creator. Use it only as a migration fallback, then keep the creation
      // record stable so the UI never attributes creation to a later editor.
      createdByUserId,
      primaryOwnerUserId: createdByUserId,
      // Existing prototype data predates an explicit default Brand field.
      // Keep the oldest visible Brand as the Workspace default during migration.
      isDefault: brand.id === defaultBrandId,
    };
  });
  const creatorIds = new Map(
    brands.flatMap((brand) =>
      brand.createdByUserId ? [[brand.id, brand.createdByUserId] as const] : [],
    ),
  );
  const membershipsWithCreators = memberships.map((membership) =>
    creatorIds.get(membership.brandId) === membership.userId
      ? { ...membership, role: "Owner" as BrandRole }
      : membership,
  );
  const missingCreatorMemberships = brands.flatMap((brand) => {
    const creatorId = brand.createdByUserId;
    if (
      !creatorId ||
      membershipsWithCreators.some(
        (membership) =>
          membership.brandId === brand.id && membership.userId === creatorId,
      )
    ) {
      return [];
    }

    return [
      { brandId: brand.id, userId: creatorId, role: "Owner" as BrandRole },
    ];
  });
  return {
    brands,
    connections,
    users: data.users.map((user) => ({ ...user })),
    memberships: [...membershipsWithCreators, ...missingCreatorMemberships],
    connectionAccessGrants: data.connectionAccessGrants.flatMap((grant) => {
      const scopedGrant = grant as BrandConnectionAccessGrant & {
        brandId?: string;
      };
      if (scopedGrant.brandId) {
        return [{ ...scopedGrant, brandId: scopedGrant.brandId }];
      }

      // v9 and older stored grants at connection level. Preserve users' access
      // during migration by applying it to every Brand that currently contains
      // that connection. New grants are always Brand-scoped.
      const connection = connections.find(
        (candidate) => candidate.id === scopedGrant.connectionId,
      );
      return (connection?.brandIds || []).map((brandId) => ({
        ...scopedGrant,
        brandId,
      }));
    }),
    pendingInvitationAccessGrants: (
      data.pendingInvitationAccessGrants ?? []
    ).map((grant) => ({
      ...grant,
      email: grant.email.trim().toLowerCase(),
    })),
    externalConnectionBrandIds: Object.fromEntries(
      Object.entries(data.externalConnectionBrandIds).map(
        ([connectionKey, value]) => [
          connectionKey,
          Array.isArray(value) ? uniqueBrandIds(value) : value ? [value] : [],
        ],
      ),
    ),
    externalConnectionBrandLabels: Object.fromEntries(
      Object.entries(data.externalConnectionBrandLabels ?? {}).map(
        ([connectionKey, labels]) => [connectionKey, { ...labels }],
      ),
    ),
    externalGoogleAdsCustomerBrandIds: Object.fromEntries(
      Object.entries(data.externalGoogleAdsCustomerBrandIds ?? {}).map(
        ([customerKey, value]) => [
          customerKey,
          Array.isArray(value) ? uniqueBrandIds(value) : value ? [value] : [],
        ],
      ),
    ),
    externalGoogleAdsCustomerBrandLabels: Object.fromEntries(
      Object.entries(data.externalGoogleAdsCustomerBrandLabels ?? {}).map(
        ([customerKey, labels]) => [customerKey, { ...labels }],
      ),
    ),
    activeAgentBrandId: data.activeAgentBrandId,
  };
};

export const getBrandConnections = (
  data: BrandPrototypeData,
  brandId: string,
): BrandConnection[] =>
  data.connections.filter((connection) =>
    connection.brandIds.includes(brandId),
  );

export const getBrandMembership = (
  data: BrandPrototypeData,
  brandId: string,
  userId: string,
): BrandMembership | null =>
  data.memberships.find(
    (membership) =>
      membership.brandId === brandId && membership.userId === userId,
  ) || null;

export const getBrandUsers = (
  data: BrandPrototypeData,
  brandId: string,
): BrandMember[] =>
  data.memberships.flatMap((membership) => {
    if (membership.brandId !== brandId) {
      return [];
    }
    const user = data.users.find(
      (candidate) => candidate.id === membership.userId,
    );
    return user ? [{ ...user, brandRole: membership.role }] : [];
  });

export const assignConnectionsToBrand = (
  data: BrandPrototypeData,
  brandId: string,
  connectionIds: string[],
  assignedByUserId: string,
  assignedAt: string,
): BrandPrototypeData => ({
  ...data,
  connections: data.connections.map((connection) =>
    connectionIds.includes(connection.id)
      ? {
          ...connection,
          brandIds: uniqueBrandIds([...connection.brandIds, brandId]),
          brandId: connection.brandIds[0] || brandId,
          assignedByUserId,
          assignedAt,
        }
      : connection,
  ),
});

export const updateConnectionBrand = (
  data: BrandPrototypeData,
  connectionId: string,
  brandIds: string[],
  assignedByUserId: string,
  assignedAt: string,
): BrandPrototypeData => ({
  ...data,
  connections: data.connections.map((connection) =>
    connection.id === connectionId
      ? {
          ...connection,
          brandIds: uniqueBrandIds(brandIds),
          brandId: uniqueBrandIds(brandIds)[0] || null,
          assignedByUserId: brandIds.length > 0 ? assignedByUserId : null,
          assignedAt: brandIds.length > 0 ? assignedAt : null,
        }
      : connection,
  ),
});

/**
 * Client-side draft used by the Brand management experience while the
 * brand-scoped connection API is being delivered. The connection is created
 * inside one Brand from the outset; it never enters the Workspace pool.
 */
export const createBrandConnection = (
  data: BrandPrototypeData,
  brandId: string,
  input: CreateBrandConnectionInput,
  connectedByUserId: string,
  connectedAt: string,
): BrandPrototypeData => ({
  ...data,
  connections: [
    ...data.connections,
    {
      id: `brand-connection-${Date.now()}`,
      provider: input.provider,
      accountName: input.accountName,
      accountDetail: input.accountDetail,
      brandId,
      brandIds: [brandId],
      status: "needs-reauthorization",
      connectedByUserId,
      connectedAt,
      assignedByUserId: connectedByUserId,
      assignedAt: connectedAt,
    },
  ],
});

export const disconnectBrandConnection = (
  data: BrandPrototypeData,
  connectionId: string,
): BrandPrototypeData => ({
  ...data,
  connections: data.connections.filter(
    (connection) => connection.id !== connectionId,
  ),
  connectionAccessGrants: data.connectionAccessGrants.filter(
    (grant) => grant.connectionId !== connectionId,
  ),
  pendingInvitationAccessGrants: (
    data.pendingInvitationAccessGrants ?? []
  ).filter((grant) => grant.connectionId !== connectionId),
});

export const setBrandMembership = (
  data: BrandPrototypeData,
  brandId: string,
  userId: string,
  role: BrandRole,
): BrandPrototypeData => ({
  ...data,
  memberships: [
    ...data.memberships.filter(
      (membership) =>
        membership.brandId !== brandId || membership.userId !== userId,
    ),
    { brandId, userId, role },
  ],
});

export const removeBrandMembership = (
  data: BrandPrototypeData,
  brandId: string,
  userId: string,
): BrandPrototypeData => {
  if (data.brands.find((brand) => brand.id === brandId)?.isDefault) {
    return data;
  }

  return {
    ...data,
    memberships: data.memberships.filter(
      (membership) =>
        membership.brandId !== brandId || membership.userId !== userId,
    ),
    // Leaving a Brand must take effect immediately: the user remains in the
    // Workspace, but no longer keeps any account grant from this Brand.
    connectionAccessGrants: data.connectionAccessGrants.filter(
      (grant) => grant.userId !== userId || grant.brandId !== brandId,
    ),
  };
};

export const deleteBrandRecord = (
  data: BrandPrototypeData,
  brandId: string,
): BrandPrototypeData => {
  return {
    ...data,
    brands: data.brands.filter((brand) => brand.id !== brandId),
    memberships: data.memberships.filter(
      (membership) => membership.brandId !== brandId,
    ),
    connectionAccessGrants: data.connectionAccessGrants.filter(
      (grant) => grant.brandId !== brandId,
    ),
    pendingInvitationAccessGrants: (
      data.pendingInvitationAccessGrants ?? []
    ).filter((grant) => grant.brandId !== brandId),
    connections: data.connections.map((connection) => {
      const brandIds = connection.brandIds.filter((id) => id !== brandId);
      return {
        ...connection,
        brandIds,
        brandId: brandIds[0] || null,
        assignedByUserId:
          brandIds.length > 0 ? connection.assignedByUserId : null,
        assignedAt: brandIds.length > 0 ? connection.assignedAt : null,
      };
    }),
    externalConnectionBrandIds: Object.fromEntries(
      Object.entries(data.externalConnectionBrandIds).map(
        ([connectionKey, brandIds]) => [
          connectionKey,
          brandIds.filter((id) => id !== brandId),
        ],
      ),
    ),
    externalGoogleAdsCustomerBrandIds: Object.fromEntries(
      Object.entries(data.externalGoogleAdsCustomerBrandIds ?? {}).map(
        ([customerKey, brandIds]) => [
          customerKey,
          brandIds.filter((id) => id !== brandId),
        ],
      ),
    ),
    activeAgentBrandId:
      data.activeAgentBrandId === brandId
        ? "all-brands"
        : data.activeAgentBrandId,
  };
};

export const getConnectionAccessGrant = (
  data: BrandPrototypeData,
  brandId: string,
  connectionId: string,
  userId: string,
): BrandConnectionAccessGrant | null =>
  data.connectionAccessGrants.find(
    (grant) =>
      grant.brandId === brandId &&
      grant.connectionId === connectionId &&
      grant.userId === userId,
  ) || null;

export const getConnectionAccessGrants = (
  data: BrandPrototypeData,
  brandId: string,
  connectionId: string,
): BrandConnectionAccessGrant[] =>
  data.connectionAccessGrants.filter(
    (grant) => grant.brandId === brandId && grant.connectionId === connectionId,
  );

export const updateConnectionAccessGrant = (
  data: BrandPrototypeData,
  brandId: string,
  connectionId: string,
  userId: string,
  accessLevel: BrandConnectionAccessLevel | null,
  canManageAccess?: boolean,
): BrandPrototypeData => {
  const existingGrant = getConnectionAccessGrant(
    data,
    brandId,
    connectionId,
    userId,
  );
  const nextAccessLevel = accessLevel || "no_access";
  const nextCanManageAccess =
    canManageAccess ?? existingGrant?.canManageAccess ?? false;
  const shouldKeepGrant =
    nextAccessLevel !== "no_access" || nextCanManageAccess;

  return {
    ...data,
    connectionAccessGrants: [
      ...data.connectionAccessGrants.filter(
        (grant) =>
          grant.brandId !== brandId ||
          grant.connectionId !== connectionId ||
          grant.userId !== userId,
      ),
      ...(shouldKeepGrant
        ? [
            {
              brandId,
              connectionId,
              userId,
              accessLevel: nextAccessLevel,
              canManageAccess: nextCanManageAccess,
            },
          ]
        : []),
    ],
  };
};

export const setPendingInvitationAccessGrants = (
  data: BrandPrototypeData,
  email: string,
  grants: Array<
    Pick<
      PendingBrandConnectionAccessGrant,
      "brandId" | "connectionId" | "accessLevel" | "canManageAccess"
    >
  >,
): BrandPrototypeData => {
  const normalizedEmail = email.trim().toLowerCase();
  return {
    ...data,
    pendingInvitationAccessGrants: [
      ...(data.pendingInvitationAccessGrants ?? []).filter(
        (grant) => grant.email !== normalizedEmail,
      ),
      ...grants.map((grant) => ({ ...grant, email: normalizedEmail })),
    ],
  };
};

/**
 * Converts email-keyed invitation drafts once the invited person appears as
 * an active Workspace user. The function is idempotent so the provider can
 * safely run it after each member refresh.
 */
export const reconcilePendingInvitationAccessGrants = (
  data: BrandPrototypeData,
  users: Array<Pick<BrandUser, "id" | "email">>,
): BrandPrototypeData => {
  const pendingGrants = data.pendingInvitationAccessGrants ?? [];
  if (pendingGrants.length === 0) {
    return data;
  }

  const userIdByEmail = new Map(
    users.map((user) => [user.email.trim().toLowerCase(), user.id] as const),
  );
  const resolvedGrants = pendingGrants.flatMap((grant) => {
    const userId = userIdByEmail.get(grant.email.trim().toLowerCase());
    return userId
      ? [
          {
            brandId: grant.brandId,
            connectionId: grant.connectionId,
            userId,
            accessLevel: grant.accessLevel,
            canManageAccess: grant.canManageAccess,
          },
        ]
      : [];
  });
  if (resolvedGrants.length === 0) {
    return data;
  }

  const resolvedKeys = new Set(
    resolvedGrants.map(
      (grant) => `${grant.brandId}:${grant.connectionId}:${grant.userId}`,
    ),
  );
  const resolvedEmails = new Set(
    pendingGrants
      .filter((grant) => userIdByEmail.has(grant.email.trim().toLowerCase()))
      .map((grant) => grant.email.trim().toLowerCase()),
  );

  return {
    ...data,
    connectionAccessGrants: [
      ...data.connectionAccessGrants.filter(
        (grant) =>
          !resolvedKeys.has(
            `${grant.brandId}:${grant.connectionId}:${grant.userId}`,
          ),
      ),
      ...resolvedGrants,
    ],
    pendingInvitationAccessGrants: pendingGrants.filter(
      (grant) => !resolvedEmails.has(grant.email.trim().toLowerCase()),
    ),
  };
};

export const updateExternalConnectionBrand = (
  data: BrandPrototypeData,
  connectionKey: string,
  brandIds: string[],
): BrandPrototypeData => ({
  ...data,
  externalConnectionBrandIds: {
    ...data.externalConnectionBrandIds,
    [connectionKey]: uniqueBrandIds(brandIds),
  },
});

export const updateExternalConnectionBrandLabels = (
  data: BrandPrototypeData,
  connectionKey: string,
  brandIds: string[],
  label: string,
): BrandPrototypeData => {
  const selectedBrandIds = uniqueBrandIds(brandIds);
  const previousLabels = data.externalConnectionBrandLabels?.[connectionKey] ?? {};
  const normalizedLabel = label.trim();
  const nextLabels = Object.fromEntries(
    selectedBrandIds.flatMap((brandId) => {
      const nextLabel = normalizedLabel || previousLabels[brandId] || '';
      return nextLabel ? [[brandId, nextLabel]] : [];
    }),
  );

  return {
    ...data,
    externalConnectionBrandLabels: {
      ...(data.externalConnectionBrandLabels ?? {}),
      [connectionKey]: nextLabels,
    },
  };
};

export const updateExternalGoogleAdsCustomerBrand = (
  data: BrandPrototypeData,
  customerKey: string,
  brandIds: string[],
): BrandPrototypeData => ({
  ...data,
  externalGoogleAdsCustomerBrandIds: {
    ...(data.externalGoogleAdsCustomerBrandIds ?? {}),
    [customerKey]: uniqueBrandIds(brandIds),
  },
});

export const isBrandOwner = (
  data: BrandPrototypeData,
  brandId: string,
  userId: string,
) => getBrandMembership(data, brandId, userId)?.role === "Owner";

export const canManageBrand = ({
  data,
  brandId,
  userId,
  isWorkspaceOwner,
}: {
  data: BrandPrototypeData;
  brandId: string;
  userId: string;
  isWorkspaceOwner: boolean;
}) => isWorkspaceOwner || isBrandOwner(data, brandId, userId);

export const getBrandConnectionAccessLevel = ({
  data,
  brandId,
  connectionId,
  userId,
}: {
  data: BrandPrototypeData;
  brandId: string;
  connectionId: string;
  userId: string;
  isWorkspaceOwner: boolean;
}): BrandConnectionAccessLevel => {
  // Workspace and integration-management roles control administration. They
  // do not silently grant access to an application's business data.
  return (
    getConnectionAccessGrant(data, brandId, connectionId, userId)
      ?.accessLevel || "no_access"
  );
};

export const updateBrandRecord = (
  data: BrandPrototypeData,
  brandId: string,
  updates: Pick<
    BrandRecord,
    "name" | "domain" | "market" | "description" | "logoUrl"
  >,
): BrandPrototypeData => ({
  ...data,
  brands: data.brands.map((brand) =>
    brand.id === brandId ? { ...brand, ...updates } : brand,
  ),
});
